import { useState, type ReactNode } from 'react';
import {
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  Printer,
  Send,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { BillingRole } from '@/hooks/admin/useAdminCustomers';
import { usePaperStatement } from '@/hooks/admin/usePaperStatement';

/**
 * Shared billing-comms + paper-statement controls.
 *
 * Extracted verbatim from the old AdminCustomers inline drawer so the
 * per-customer detail page can host them under dedicated tabs. They call the
 * exact same backend endpoints (admin-set-billing-comms via the parent's
 * onSave, admin-send-statement via onSendStatement, admin-send-paper-statement
 * via usePaperStatement). No data-model change.
 */

export interface BillingCommsEdit {
  billing_email?: string | null;
  accounting_email?: string | null;
  remittance_email?: string | null;
  optional_email?: string | null;
  reminders_enabled?: boolean;
  statements_enabled?: boolean;
  invoice_recipients?: BillingRole[];
  statement_recipients?: BillingRole[];
  reminder_recipients?: BillingRole[];
  order_update_recipients?: BillingRole[];
  paper_statements_enabled?: boolean;
  paper_invoices_enabled?: boolean;
  billing_doc_type?: 'statement' | 'invoice' | 'both';
}

export interface StatementResult {
  sent: boolean;
  open_count: number;
  open_total: number;
  skipped_reason?: string;
  /** True when it went to a test address rather than the customer. */
  test_send?: boolean;
  recipients?: string[];
}

/** Subset of customer fields the billing editor reads to seed its form. */
export interface BillingCommsSource {
  billing_email: string | null;
  accounting_email: string | null;
  remittance_email: string | null;
  optional_email: string | null;
  reminders_enabled: boolean | null;
  statements_enabled: boolean | null;
  invoice_recipients: BillingRole[] | null;
  statement_recipients: BillingRole[] | null;
  reminder_recipients: BillingRole[] | null;
  order_update_recipients: BillingRole[] | null;
  paper_statements_enabled: boolean | null;
  paper_invoices_enabled: boolean | null;
  billing_doc_type?: 'statement' | 'invoice' | 'both' | null;
}

/**
 * A member store's invoice routing always carries BOTH the group's Accounting
 * address and the store's OWN Primary — Sky, 2026-09-09: "each store has to
 * get a copy of their invoice."
 *
 * ⚠ Mirrors `storeInvoiceRoles` in netlify/functions/_lib/customer-group.ts.
 *   The server is the guarantee — `groupLockViolation` REFUSES a locked
 *   store's patch that drops either role — so this only keeps the form from
 *   sending one it would reject. `tests/group-invoice-copy.test.ts` fails if
 *   the two stop agreeing.
 */
function lockedInvoiceRoles(roles: BillingRole[]): BillingRole[] {
  const out = new Set<BillingRole>(roles);
  out.add('accounting');
  out.add('billing');
  return (['billing', 'remittance', 'optional', 'accounting'] as BillingRole[]).filter((r) =>
    out.has(r),
  );
}

const BILLING_DOC_TYPES: { key: 'statement' | 'invoice' | 'both'; label: string; desc: string }[] = [
  { key: 'statement', label: 'Statement', desc: 'One open-items account statement' },
  { key: 'invoice', label: 'Invoices', desc: 'Each open invoice, sent individually' },
  { key: 'both', label: 'Both', desc: 'A statement and the open invoices' },
];

const DOC_TYPES: { key: DocTypeKey; label: string }[] = [
  { key: 'invoice', label: 'Invoices' },
  { key: 'statement', label: 'Statements' },
  { key: 'reminder', label: 'Reminders' },
  { key: 'order_update', label: 'Order updates' },
];

type DocTypeKey = 'invoice' | 'statement' | 'reminder' | 'order_update';

/**
 * UI vocabulary for the four contact-email slots. Storage keeps the legacy
 * column/role names (billing / remittance / optional / accounting) — only the
 * labels changed (2026-07-08).
 */
const EMAIL_SLOTS: { role: BillingRole; label: string; placeholder: string }[] = [
  { role: 'billing', label: 'Primary Email', placeholder: 'owner@customer.com' },
  { role: 'remittance', label: 'Secondary Email', placeholder: 'office@customer.com' },
  { role: 'optional', label: 'Optional Email', placeholder: 'extra@customer.com' },
  { role: 'accounting', label: 'Accounting Email', placeholder: 'ap@customer.com' },
];

function fmtUsd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function BillingCommsEditor({
  source,
  saving,
  onSave,
  onSendStatement,
  sendingStatement,
  statementResult,
  groupLock,
}: {
  source: BillingCommsSource;
  saving: boolean;
  onSave: (input: BillingCommsEdit) => void;
  /** Called with addresses for a TEST send, or nothing to send to the customer. */
  onSendStatement: (to?: string[]) => void;
  sendingStatement: boolean;
  statementResult: StatementResult | null;
  /** Member store of a Master-billing group: accounting email + the
   * Accounting slot of invoice routing + statements/paper are locked to
   * the master's rules (change them on the master account). */
  groupLock?: { masterName: string } | null;
}) {
  const [statementTestTo, setStatementTestTo] = useState('');
  const [primary, setPrimary] = useState(source.billing_email ?? '');
  const [accounting, setAccounting] = useState(source.accounting_email ?? '');
  const [remittance, setRemittance] = useState(source.remittance_email ?? '');
  const [optional, setOptional] = useState(source.optional_email ?? '');
  const [reminders, setReminders] = useState(source.reminders_enabled === true);
  const [statements, setStatements] = useState(source.statements_enabled === true);
  const [invoiceRoles, setInvoiceRoles] = useState<BillingRole[]>(
    source.invoice_recipients ?? ['billing'],
  );
  const [statementRoles, setStatementRoles] = useState<BillingRole[]>(
    source.statement_recipients ?? ['billing'],
  );
  const [reminderRoles, setReminderRoles] = useState<BillingRole[]>(
    source.reminder_recipients ?? ['billing'],
  );
  const [orderUpdateRoles, setOrderUpdateRoles] = useState<BillingRole[]>(
    source.order_update_recipients ?? ['billing'],
  );
  const [paperStatements, setPaperStatements] = useState(source.paper_statements_enabled === true);
  const [paperInvoices, setPaperInvoices] = useState(source.paper_invoices_enabled === true);
  const [docType, setDocType] = useState<'statement' | 'invoice' | 'both'>(
    source.billing_doc_type === 'invoice' || source.billing_doc_type === 'both'
      ? source.billing_doc_type
      : 'statement',
  );

  const rolesFor = (key: DocTypeKey) =>
    key === 'invoice'
      ? invoiceRoles
      : key === 'statement'
        ? statementRoles
        : key === 'reminder'
          ? reminderRoles
          : orderUpdateRoles;
  const setRolesFor = (key: DocTypeKey, roles: BillingRole[]) =>
    key === 'invoice'
      ? setInvoiceRoles(roles)
      : key === 'statement'
        ? setStatementRoles(roles)
        : key === 'reminder'
          ? setReminderRoles(roles)
          : setOrderUpdateRoles(roles);

  const toggleRole = (key: DocTypeKey, role: BillingRole) => {
    const cur = rolesFor(key);
    setRolesFor(
      key,
      cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role],
    );
  };

  const emailValueByRole: Record<BillingRole, string> = {
    billing: primary,
    remittance: remittance,
    optional: optional,
    accounting: accounting,
  };
  const setEmailByRole: Record<BillingRole, (v: string) => void> = {
    billing: setPrimary,
    remittance: setRemittance,
    optional: setOptional,
    accounting: setAccounting,
  };

  const locked = !!groupLock;
  // Set once staff typed the confirmation word to drop an always-on invoice
  // copy; the server refuses the removal without it.
  const [removalConfirmed, setRemovalConfirmed] = useState(false);
  const onToggle = (key: DocTypeKey, role: BillingRole) => {
    const protectedBox =
      locked && key === 'invoice' && (role === 'billing' || role === 'accounting');
    if (protectedBox && rolesFor('invoice').includes(role)) {
      const who =
        role === 'billing'
          ? 'this store will stop receiving its OWN invoices'
          : `${groupLock?.masterName}'s Accounting address will stop receiving this store's invoices`;
      const typed = window.prompt(
        `Warning: ${who}.\n\nType REMOVE to continue, or Cancel to keep it.`,
      );
      if (typed?.trim().toUpperCase() !== 'REMOVE') return;
      setRemovalConfirmed(true);
    }
    toggleRole(key, role);
  };

  const handleSave = () => {
    const edit: BillingCommsEdit = {
      billing_email: primary.trim() === '' ? null : primary.trim(),
      remittance_email: remittance.trim() === '' ? null : remittance.trim(),
      optional_email: optional.trim() === '' ? null : optional.trim(),
      reminders_enabled: reminders,
      // Locked stores: invoices + statements are the master's, so the store
      // never writes its own routing for them.
      // ⚠ This used to send `['accounting']` verbatim, which DISCARDED the
      //   store's own Primary on every save — one of the three places that
      //   kept corporate as the only recipient of a store's invoice. A locked
      //   store keeps both, plus anything else it had chosen.
      invoice_recipients: locked && !removalConfirmed ? lockedInvoiceRoles(invoiceRoles) : invoiceRoles,
      ...(locked && removalConfirmed ? { confirm_invoice_removal: 'REMOVE' } : {}),
      // Omitted entirely on a locked store: the field isn't shown as editable,
      // so the form must not overwrite the stored target either.
      ...(locked ? {} : { statement_recipients: statementRoles }),
      // A locked store never routes its own order traffic to the master's
      // Accounting address — strip it rather than trusting the (now disabled)
      // checkbox. Corporate AP gets invoices, not every store's reminders.
      reminder_recipients: locked
        ? reminderRoles.filter((r) => r !== 'accounting')
        : reminderRoles,
      order_update_recipients: locked
        ? orderUpdateRoles.filter((r) => r !== 'accounting')
        : orderUpdateRoles,
      billing_doc_type: docType,
    };
    if (locked) {
      // Locked on member stores — managed at the master account. The
      // accounting email is omitted entirely (the server 409s any change);
      // statements/paper are forced off.
      edit.statements_enabled = false;
      edit.paper_statements_enabled = false;
      edit.paper_invoices_enabled = false;
    } else {
      edit.accounting_email = accounting.trim() === '' ? null : accounting.trim();
      edit.statements_enabled = statements;
      edit.paper_statements_enabled = paperStatements;
      edit.paper_invoices_enabled = paperInvoices;
    }
    onSave(edit);
  };

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-4 space-y-4">
      {/* Contact emails — one row per address, with per-email doc-type routing */}
      <div>
        <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight mb-2">
          Contact emails — what each address receives
        </div>
        <div className="divide-y divide-brix-border overflow-hidden rounded-lg border border-brix-border">
          {EMAIL_SLOTS.map(({ role, label, placeholder }) => {
            const emailLocked = locked && role === 'accounting';
            return (
              <div
                key={role}
                className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-3 py-2.5 bg-brix-surface"
              >
                <div className="sm:w-64 shrink-0">
                  <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
                    {label}
                    {emailLocked && <span className="ml-1 normal-case">🔒 master</span>}
                  </label>
                  <Input
                    value={emailValueByRole[role]}
                    onChange={(e) => setEmailByRole[role](e.target.value)}
                    placeholder={placeholder}
                    className="mt-1"
                    type="email"
                    disabled={emailLocked}
                    title={
                      emailLocked
                        ? `Managed on ${groupLock?.masterName} (Master billing)`
                        : undefined
                    }
                  />
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  {DOC_TYPES.map(({ key, label: docLabel }) => {
                    // On a member store the master owns two things on this
                    // grid: the Accounting ADDRESS (every doc type on that
                    // row) and the statement routing on every row. Invoices
                    // are pinned ON for the two addresses that must always get
                    // one — the store's own Primary and the master's
                    // Accounting — and OPEN on Secondary / Optional, so staff
                    // can add a copy (a GM, a bookkeeper) without unlocking
                    // anything. Reminders and order updates on the store's own
                    // addresses stay editable — that is the store's order
                    // traffic; the Accounting row's stays locked so a store
                    // can't point its order updates at corporate AP.
                    //
                    // ⚠ This used to be `key === 'invoice' || …`, which greyed
                    //   out the Invoices box on EVERY row of a locked store —
                    //   the two required copies AND the optional ones — so a
                    //   Melt store could never be given a second invoice
                    //   recipient at all (Sky, 2026-09-23: "anyone we select
                    //   should be able to get invoices. but only accounting and
                    //   store email stay locked"). The server never refused
                    //   the extra role; only the checkbox did.
                    const masterManaged =
                      locked &&
                      (key === 'statement' || (role === 'accounting' && key !== 'invoice'));
                    // Primary + Accounting invoice boxes stay editable but
                    // guarded: unchecking asks for a typed confirmation.
                    const checked = masterManaged ? false : rolesFor(key).includes(role);
                    return (
                      <label
                        key={key}
                        className={cn(
                          'flex items-center gap-1.5 text-xs text-brix-ink',
                          masterManaged ? 'opacity-70' : 'cursor-pointer',
                        )}
                        title={
                          masterManaged
                            ? role === 'accounting'
                                ? `The Accounting address and what it receives are managed on ${groupLock?.masterName}`
                                : `Statements are sent from ${groupLock?.masterName} for the whole group — not set per store`
                            : undefined
                        }
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={masterManaged}
                          onChange={() => onToggle(key, role)}
                          className="accent-brix-primary cursor-pointer disabled:cursor-not-allowed"
                        />
                        {docLabel}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-brix-muted mt-1.5">
          {locked ? (
            <>
              Reminders and order updates go to this store&rsquo;s own addresses. Invoices always
              email <span className="font-semibold">both</span> this store&rsquo;s own Primary
              Email and {groupLock?.masterName}&rsquo;s Accounting address &mdash; every store gets
              a copy of its own invoice &mdash; and you can tick Invoices on the Secondary or
              Optional email to send a copy there too. Statements are sent from{' '}
              {groupLock?.masterName} for the whole group &mdash; not set here.{' '}
              <span className="font-semibold">Order updates</span> do NOT fall back to the Primary
              Email &mdash; uncheck them everywhere and this store gets no order status emails.
            </>
          ) : (
            <>
              Each checked type is emailed to that address. Invoices / Statements / Reminders with
              nothing checked fall back to the Primary Email. <span className="font-semibold">Order
              updates</span> (order received / scheduled / out for delivery / delivered) do NOT fall
              back &mdash; uncheck them everywhere and the customer gets no order status emails. The
              person who places an order always receives them (unless they&rsquo;re Brix staff).
            </>
          )}
        </p>
      </div>

      {/* Reminder + statement toggles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <FeatureToggle
          icon={<Mail size={14} />}
          label="Overdue reminders"
          desc="Daily due-soon + past-due nudges (opt-in)"
          enabled={reminders}
          saving={false}
          onChange={setReminders}
        />
        <FeatureToggle
          icon={<FileText size={14} />}
          label="Monthly statements"
          desc={
            locked
              ? `Locked off — billing runs through ${groupLock?.masterName}'s Accounting email`
              : 'Open-items statement on the 5th (opt-in)'
          }
          enabled={locked ? false : statements}
          saving={locked}
          onChange={setStatements}
        />
      </div>

      {/* Billing Run document type — what the bulk run sends */}
      <div>
        <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight mb-2">
          Billing Run sends
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          {BILLING_DOC_TYPES.map(({ key, label, desc }) => {
            const active = docType === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setDocType(key)}
                className={cn(
                  'text-left rounded-lg border px-3 py-2.5 cursor-pointer transition-all',
                  active
                    ? 'border-brix-primary bg-brix-primary-tint'
                    : 'border-brix-border bg-brix-surface hover:border-brix-primary/40',
                )}
              >
                <div className="text-sm font-semibold text-brix-ink">{label}</div>
                <div className="text-[11px] text-brix-muted mt-0.5 leading-tight">{desc}</div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-brix-muted mt-1.5">
          Drives the bulk <span className="font-semibold">Billing Run</span>: which document this
          customer gets. Channels still follow the toggles above (email needs statements/invoice
          emails enabled; paper needs the opt-in below).
        </p>
      </div>

      {/* Customer paper-mail prefs (postal) — set on the customer's behalf */}
      <div>
        <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight mb-2">
          Paper mail (postal) — customer's preference
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <FeatureToggle
            icon={<Printer size={14} />}
            label="Paper statements"
            desc={locked ? 'Locked off for Master-billing stores' : 'Customer wants statements mailed (USPS)'}
            enabled={locked ? false : paperStatements}
            saving={locked}
            onChange={setPaperStatements}
          />
          <FeatureToggle
            icon={<Printer size={14} />}
            label="Paper invoice copies"
            desc={locked ? 'Locked off for Master-billing stores' : 'Customer wants invoice copies mailed (USPS)'}
            enabled={locked ? false : paperInvoices}
            saving={locked}
            onChange={setPaperInvoices}
          />
        </div>
        <p className="text-[11px] text-brix-muted mt-1.5">
          These reflect the customer's opt-in for physical mail. You can still mail anything to them
          regardless. The Billing Run “Everyone who opted into paper” audience uses the statements
          flag.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : null}
            {saving ? 'Saving…' : 'Save billing comms'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // This mails the CUSTOMER. On a chain master it mails their AP
              // department the whole chain's balance — not something to
              // discover after clicking.
              const who = statementTestTo.trim()
                ? `a test copy to ${statementTestTo.trim()}`
                : "the statement to this customer's billing recipients";
              if (window.confirm(`Send ${who}?`)) {
                onSendStatement(
                  statementTestTo.trim() ? [statementTestTo.trim()] : undefined,
                );
              }
            }}
            disabled={sendingStatement}
            title={
              statementTestTo.trim()
                ? 'Send a test copy to the address on the left'
                : 'Send an open-items statement to this customer right now'
            }
          >
            {sendingStatement ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Send size={12} />
            )}
            {sendingStatement
              ? 'Sending…'
              : statementTestTo.trim()
                ? 'Send test copy'
                : 'Send statement now'}
          </Button>
          {/* Type an address and the same button sends the customer's REAL
              statement there instead — the only way to see what they will
              receive without mailing them. */}
          <input
            type="email"
            value={statementTestTo}
            onChange={(e) => setStatementTestTo(e.target.value)}
            placeholder="test copy to me@…  (optional)"
            className="h-8 w-56 rounded-lg border border-brix-border bg-brix-surface px-2.5 text-xs text-brix-ink placeholder:text-brix-muted"
          />
        </div>
        {statementResult && (
          <div className="text-xs text-brix-muted">
            {statementResult.sent
              ? `${statementResult.test_send ? 'Test copy sent' : 'Sent'} · ${statementResult.open_count} open · ${fmtUsd(statementResult.open_total)}${
                  statementResult.recipients?.length
                    ? ` → ${statementResult.recipients.join(', ')}`
                    : ''
                }`
              : `Not sent — ${statementResult.skipped_reason ?? 'no open invoices'}`}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * PaperStatementAction — preview-first DocuPost paper-mail control. Clicking
 * "Preview PDF" renders + opens the PDF in a new tab (no postage). Once a
 * preview exists, a "Send for real" confirm button mails it via DocuPost.
 */
export function PaperStatementAction({
  customerId,
  customerName,
}: {
  customerId: string | null;
  customerName: string;
}) {
  const mutation = usePaperStatement();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (!customerId) return null;

  const previewing =
    mutation.isPending && mutation.variables?.mode === 'preview';
  const sending = mutation.isPending && mutation.variables?.mode === 'send';
  const errorMsg = mutation.error instanceof Error ? mutation.error.message : null;

  const handlePreview = () => {
    setSent(false);
    mutation.mutate(
      { customer_id: customerId, mode: 'preview' },
      {
        onSuccess: (res) => {
          setPreviewUrl(res.pdf_url);
          if (typeof window !== 'undefined') window.open(res.pdf_url, '_blank', 'noopener');
        },
      },
    );
  };

  const handleSend = () => {
    if (
      !window.confirm(
        `Mail a paper statement to ${customerName} via DocuPost? This spends real postage and cannot be undone.`,
      )
    ) {
      return;
    }
    mutation.mutate(
      { customer_id: customerId, mode: 'send' },
      {
        onSuccess: (res) => {
          if (res.mode === 'send' && res.ok) setSent(true);
        },
      },
    );
  };

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-4 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <FileText size={14} className="text-brix-primary" />
        <span className="text-sm font-semibold text-brix-ink">Paper statement</span>
        <span className="text-[11px] text-brix-muted">DocuPost · mailed USPS first class</span>
      </div>
      <p className="text-[11px] text-brix-muted">
        Preview is free. Postage is only spent when you click “Send for real”.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="secondary" size="sm" onClick={handlePreview} disabled={mutation.isPending}>
          {previewing ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
          {previewing ? 'Rendering…' : 'Preview PDF'}
        </Button>
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-brix-primary hover:underline"
          >
            <ExternalLink size={12} /> Open preview
          </a>
        )}
        {previewUrl && !sent && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            disabled={mutation.isPending}
            className="text-red-50 bg-red-600 hover:bg-red-700"
            title="Mail this statement via DocuPost (spends postage)"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {sending ? 'Mailing…' : 'Send for real'}
          </Button>
        )}
      </div>
      {sent && <div className="text-xs text-emerald-700">Mailed via DocuPost.</div>}
      {errorMsg && <div className="text-xs text-red-600">{errorMsg}</div>}
    </div>
  );
}

export function FeatureToggle({
  icon,
  label,
  desc,
  enabled,
  saving,
  onChange,
}: {
  icon: ReactNode;
  label: string;
  desc: string;
  enabled: boolean;
  saving: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-brix-border bg-brix-surface px-3 py-2.5">
      <div className="min-w-0 flex items-start gap-2">
        <span className="text-brix-primary mt-0.5 shrink-0">{icon}</span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-brix-ink leading-tight">{label}</div>
          <div className="text-[11px] text-brix-muted leading-tight mt-0.5">{desc}</div>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={`${label}: ${enabled ? 'on' : 'off'}`}
        disabled={saving}
        onClick={() => onChange(!enabled)}
        className={cn(
          'relative shrink-0 w-10 h-6 rounded-full transition-colors cursor-pointer disabled:opacity-50',
          enabled ? 'bg-brix-primary' : 'bg-slate-300',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
            enabled && 'translate-x-4',
          )}
        />
      </button>
    </div>
  );
}
